import React from "react";
import { CircleMarker, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { Link } from "react-router-dom";
import { TYPE_META, classifyType, formatAcres } from "../../lib/incidents";
import { sameFire } from "../../lib/dedupe";
import { StatusBadge, TypeBadge } from "../Badges";
import FireNameLabel from "./FireNameLabel";

const _flameCache = {};
export function flameIcon(color, size, stage) {
  const key = `${color}-${size}-${stage || ""}`;
  if (!_flameCache[key]) {
    const ringCls =
      stage === "new" ? "fire-ring-new" : stage === "recent" ? "fire-ring-recent" : stage === "grey" ? "fire-grey" : "";
    _flameCache[key] = L.divIcon({
      className: "flame-marker",
      html: `<span class="fire-lifecycle ${ringCls}"><svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.65));">
        <path d="M12 1.5c.6 3.2-.9 5-2.5 6.8C7.7 10.3 6 12.2 6 15a6 6 0 0 0 12 0c0-2.2-.9-3.9-2-5.5-.4 1-.9 1.7-1.8 2.3.3-3.6-1-8-2.2-10.3z" fill="${color}" stroke="#0A0D0C" stroke-width="1.4" stroke-linejoin="round"/>
        <path d="M12 20.4a3 3 0 0 1-3-3c0-1.4.8-2.3 1.6-3.2.5.7 1 1.1 1.6 1.4-.1-1.1.2-2.4.8-3.4 1.1 1.4 2 2.8 2 5.2a3 3 0 0 1-3 3z" fill="#FFD166" opacity="0.92"/>
      </svg></span>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -size / 2 + 2],
    });
  }
  return _flameCache[key];
}

const GREY_STATUSES = ["CONTAINED", "CONTROLLED", "OUT"];
const RING_MS = 45 * 60e3;

function fireStage(inc, updatedIso) {
  const status = (inc.status_label || "").toUpperCase();
  if (GREY_STATUSES.some((s) => status.includes(s))) return "grey";
  const newest = updatedIso || inc.date;
  if (newest && Date.now() - new Date(newest).getTime() > 7 * 24 * 3600e3) return "grey";
  // Red blinking ring for 45 min from when it became a wildfire (promotion or report)
  const start = inc.promoted_at || inc.date;
  if (start) {
    const ageMs = Date.now() - new Date(start).getTime();
    if (ageMs >= 0 && ageMs < RING_MS) return "new";
  }
  return "";
}

// Orange blinking ring for new non-wildfire reports (smoke checks etc.), 45 min
function orangeRing(inc) {
  if (!inc.date) return false;
  const ageMs = Date.now() - new Date(inc.date).getTime();
  return ageMs >= 0 && ageMs < RING_MS;
}

let _ringIcon = null;
function ringOverlayIcon() {
  if (!_ringIcon) {
    _ringIcon = L.divIcon({
      className: "ring-overlay",
      html: '<span class="fire-lifecycle fire-ring-orange"></span>',
      iconSize: [26, 26],
      iconAnchor: [13, 13],
    });
  }
  return _ringIcon;
}

function fmtDateTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function markerRadius(isActive, isFire) {
  if (isActive) return isFire ? 14 : 10.5;
  if (isFire) return 8;
  return 6;
}

// Acreage-scaled flame size — same scale as DNR flames. Unknown acreage
// renders medium (26) so fresh reports with no size yet stay visible.
export function flameSize(acres) {
  if (acres == null || acres === "") return 26;
  const a = Number(acres) || 0;
  if (a >= 1000) return 34;
  if (a >= 100) return 28;
  if (a >= 10) return 24;
  if (a >= 1) return 20;
  return 18;
}

export default function IncidentLayer({ incidents, activeId, onMarkerClick, onMarkerHover, nifcIncidents = [] }) {
  return incidents.map((inc) => {
    const kind = classifyType(inc.type);
    const meta = TYPE_META[kind];
    const isActive = inc.id === activeId;
    const isFire = kind === "WILDFIRE";
    // Freshest known update: WildCAD change stamp or matching NIFC record.
    // Only shown when genuinely newer than the report time (no duplicate lines).
    const nifcMod = nifcIncidents.find((n) => sameFire(inc, n))?.modified_at || null;
    const updatedIso = [inc.last_changed_at, nifcMod]
      .filter(Boolean)
      .sort((a, b) => new Date(a) - new Date(b))
      .pop();
    const showUpdated = updatedIso && (!inc.date || new Date(updatedIso) > new Date(inc.date));
    const popup = (
      <Popup>
        <div className="min-w-[220px]" data-testid={`map-popup-${inc.id}`}>
          <div className="flex items-center gap-1.5 mb-2 flex-wrap">
            <TypeBadge type={inc.type} />
            <StatusBadge status={inc.status_label} />
          </div>
          <div className="font-display font-bold text-[15px] mb-1 text-tac-text leading-snug">
            {inc.name}
          </div>
          <div className="text-[14px] text-tac-dim space-y-0.5 mb-2 font-mono-tac tabular">
            {inc.location && <div>{inc.location}</div>}
            <div>{formatAcres(inc.acres)} acres · {inc.fuels || "—"}</div>
            {inc.date && (
              <div className="text-tac-faint">Reported {fmtDateTime(inc.date)}</div>
            )}
            {showUpdated && (
              <div className="text-tac-faint" data-testid={`map-popup-updated-${inc.id}`}>
                Updated {fmtDateTime(updatedIso)}
              </div>
            )}
            <div>{inc.latitude?.toFixed(4)}, {inc.longitude?.toFixed(4)}</div>
          </div>
          <Link
            to={`/incident/${inc.id}`}
            data-testid={`map-popup-detail-link-${inc.id}`}
            className="inline-flex text-[11px] font-bold uppercase tracking-wider text-tac-primary hover:underline"
          >
            View Details →
          </Link>
        </div>
      </Popup>
    );
    if (isFire) {
      const base = flameSize(inc.acres);
      return (
        <Marker
          key={inc.id}
          pane="fire-icons"
          position={[inc.latitude, inc.longitude]}
          icon={flameIcon(meta.color, isActive ? Math.round(base * 1.75) : base, fireStage(inc, updatedIso))}
          eventHandlers={{
            click: () => onMarkerClick && onMarkerClick(inc.id),
            mouseover: () => onMarkerHover && onMarkerHover(inc.id),
            mouseout: () => onMarkerHover && onMarkerHover(null),
          }}
        >
          {popup}
          <FireNameLabel name={inc.name} acres={inc.acres} />
        </Marker>
      );
    }
    return (
      <React.Fragment key={inc.id}>
        <CircleMarker
          center={[inc.latitude, inc.longitude]}
          radius={markerRadius(isActive, isFire)}
          pathOptions={{
            color: "#0A0D0C",
            weight: 2,
            fillColor: meta.color,
            fillOpacity: isActive ? 1 : 0.92,
          }}
          eventHandlers={{ click: () => onMarkerClick && onMarkerClick(inc.id) }}
        >
          {popup}
        </CircleMarker>
        {orangeRing(inc) && (
          <Marker
            position={[inc.latitude, inc.longitude]}
            icon={ringOverlayIcon()}
            interactive={false}
            zIndexOffset={400}
          />
        )}
      </React.Fragment>
    );
  });
}
